import { type Editor } from '@tiptap/react';
import {
  flip,
  shift,
  offset,
  useRole,
  useHover,
  useFocus,
  autoUpdate,
  useDismiss,
  safePolygon,
  useFloating,
  useInteractions,
  useTransitionStyles,
} from '@floating-ui/react';
import { type CSSProperties, useState } from 'react';

import { ToCRailPanel } from './toc-rail-panel';
import { useEditorTocData } from './use-editor-toc-data';

import styles from './toc-rail.module.scss';

const MARKER_WIDTHS = [12, 8, 10, 12, 8] as const;
const ARROW_TOP = 19;
const OPEN_DURATION_MS = 180;
const CLOSE_DURATION_MS = 120;

const ARROW_STYLE: CSSProperties = {
  position: 'absolute',
  top: ARROW_TOP,
  pointerEvents: 'none',
};

export const ToCRail = (props: ToCRailProps) => {
  const { editor } = props;

  const [open, setOpen] = useState(false);
  const headings = useEditorTocData(editor, { live: open });

  const { refs, floatingStyles, context, placement } = useFloating({
    placement: 'left-start',
    strategy: 'fixed',
    open,
    onOpenChange: setOpen,
    middleware: [
      offset({ mainAxis: 4, crossAxis: -20 }),
      flip({ padding: 10, fallbackPlacements: ['left-start', 'left', 'left-end'] }),
      shift({ padding: 10 }),
    ],
    whileElementsMounted: autoUpdate,
  });
  const hover = useHover(context, {
    move: false,
    handleClose: safePolygon({ blockPointerEvents: false, buffer: 0.5 }),
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'dialog' });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);
  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: { open: OPEN_DURATION_MS, close: CLOSE_DURATION_MS },
    initial: { opacity: 0, transform: 'scale(0.96)' },
    common: ({ side }) => ({
      transformOrigin: {
        top: 'bottom',
        bottom: 'top',
        left: 'right',
        right: 'left',
      }[side],
    }),
  });

  if (!headings.length) {
    return null;
  }

  const side = placement.split('-')[0] ?? 'left';

  return (
    <>
      <div
        className={styles.tocMarkers}
        data-testid="toc-rail"
        {...getReferenceProps({
          ref: refs.setReference,
          tabIndex: 0,
          'aria-label': 'Table of contents',
        })}
      >
        {MARKER_WIDTHS.map((markerWidth, index) => (
          <div key={index} style={{ width: markerWidth }} className={styles.tocMarker} data-testid="toc-marker" />
        ))}
      </div>
      <ToCRailPanel
        side={side}
        editor={editor}
        headings={headings}
        isMounted={isMounted}
        arrowStyle={ARROW_STYLE}
        floatingStyles={floatingStyles}
        setFloatingNode={refs.setFloating}
        transitionStyles={transitionStyles}
        getFloatingProps={getFloatingProps}
      />
    </>
  );
};

interface ToCRailProps {
  editor: Editor | null;
}
